import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { viewNotificationDetails } from '../../slice/notificationSlice'

// ─── Config ───────────────────────────────────────────────────────────────────
const ROLE_COLORS = {
    tutor: { bg: '#dbeafe', color: '#1d4ed8', label: 'Tutor' },
    parent: { bg: '#fce7f3', color: '#be185d', label: 'Parent' },
    null: { bg: '#f1f5f9', color: '#64748b', label: 'User' },
}

const RECIPIENT_CONFIG = {
    All: { bg: '#dcfce7', color: '#15803d', icon: '🌐', label: 'All Users' },
    tutor: { bg: '#dbeafe', color: '#1d4ed8', icon: '🎓', label: 'All Tutors' },
    parent: { bg: '#fce7f3', color: '#be185d', icon: '👨‍👩‍👧', label: 'All Parents' },
    specific: { bg: '#fef9c3', color: '#a16207', icon: '🎯', label: 'Specific Users' },
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString('en-US', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    })
}

// ─── Recipient Row ─────────────────────────────────────────────────────────────
const RecipientRow = ({ recipient, index }) => {
    const conf = ROLE_COLORS[recipient.role] ?? ROLE_COLORS[null]
    const initial = recipient.name?.charAt(0)?.toUpperCase() ?? '?'

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 8,
                backgroundColor: index % 2 === 0 ? '#fafafa' : '#fff',
                border: '1px solid #f1f5f9',
                transition: 'background 0.15s',
            }}
        >
            {/* Avatar */}
            <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                backgroundColor: conf.bg,
                color: conf.color,
                fontWeight: 700, fontSize: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${conf.color}33`,
            }}>
                {initial}
            </div>

            {/* Name + sub info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                    fontSize: 14, fontWeight: 600, color: '#111827',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {recipient.name}
                </div>
                {recipient.sub_name && (
                    <div style={{
                        fontSize: 12, color: '#9ca3af', marginTop: 1,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {recipient.sub_name}
                    </div>
                )}
            </div>

            {/* Role badge */}
            <span style={{
                fontSize: 10, fontWeight: 700, padding: '3px 9px',
                borderRadius: 20, flexShrink: 0,
                backgroundColor: conf.bg, color: conf.color,
                textTransform: 'capitalize', letterSpacing: '0.04em',
            }}>
                {conf.label}
            </span>
        </div>
    )
}

// ─── Scrollable Recipients Panel ──────────────────────────────────────────────
const RecipientsPanel = ({ recipients, title, subtitle }) => {
    if (!recipients?.length) return null

    return (
        <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
            <div className="card-body p-4">

                {/* Header */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                    <h6 className="fw-semibold mb-0" style={{ color: '#111827' }}>{title}</h6>
                    <span style={{
                        fontSize: 12, fontWeight: 600, padding: '2px 10px',
                        borderRadius: 20, backgroundColor: '#f1f5f9', color: '#64748b',
                    }}>
                        {recipients.length} {recipients.length === 1 ? 'person' : 'people'}
                    </span>
                </div>

                {subtitle && (
                    <p className="text-muted mb-3" style={{ fontSize: 13 }}>{subtitle}</p>
                )}

                {/* Scrollable list — fixed height so it doesn't blow out the layout */}
                <div style={{
                    maxHeight: '320px',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    paddingRight: 2,   // space for scrollbar
                    // Custom thin scrollbar
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#d1d5db transparent',
                }}>
                    {recipients.map((r, i) => (
                        <RecipientRow key={`${r.role}-${r.id}`} recipient={r} index={i} />
                    ))}
                </div>

                {/* Overflow hint */}
                {recipients.length > 6 && (
                    <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, marginBottom: 0, textAlign: 'center' }}>
                        Scroll to see all {recipients.length} recipients
                    </p>
                )}
            </div>
        </div>
    )
}

// ─── Main Component ───────────────────────────────────────────────────────────
const ViewNotification = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const location = useLocation()
    const id = location.state?.notificationId

    const { notificationDetail, loading } = useSelector((state) => state.notification)

    useEffect(() => {
        if (id) dispatch(viewNotificationDetails({ id }))
    }, [id, dispatch])

    const detail = notificationDetail || {}

    // resolved_recipients comes from backend — already has { id, role, name, sub_name }
    const resolved = detail.resolved_recipients ?? []

    const recipConf = RECIPIENT_CONFIG[detail.type] ?? RECIPIENT_CONFIG.specific

    // For summary counts
    const tutorCount = resolved.filter((r) => r.role === 'tutor').length
    const parentCount = resolved.filter((r) => r.role === 'parent').length

    // Subtitle for mixed panels
    const mixedSubtitle = detail.type === 'tutor'
        ? 'These specific parents were added alongside all tutors'
        : detail.type === 'parent'
            ? 'These specific tutors were added alongside all parents'
            : 'Individual users this notification was sent to'

    if (loading) {
        return (
            <div className="container-fluid px-4 py-3">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
                    <div className="spinner-border text-success" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container-fluid px-4 py-3">

            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Notification Details</h4>
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate('/notification')}
                    style={{ padding: '8px 20px', fontSize: '14px', borderRadius: '6px' }}
                >
                    ← Back to List
                </button>
            </div>

            <div className="row g-4">

                {/* Left col — main content */}
                <div className="col-lg-8">

                    {/* Title + Message */}
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
                        <div className="card-body p-4">

                            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                <span style={{
                                    fontSize: 12, fontWeight: 700, padding: '4px 12px',
                                    borderRadius: 20, backgroundColor: recipConf.bg,
                                    color: recipConf.color, letterSpacing: '0.03em',
                                }}>
                                    {recipConf.icon}&nbsp;&nbsp;{recipConf.label}
                                </span>
                                <span style={{
                                    fontSize: 12, fontWeight: 600, padding: '4px 12px',
                                    borderRadius: 20,
                                    backgroundColor: detail.is_send ? '#dcfce7' : '#fef9c3',
                                    color: detail.is_send ? '#15803d' : '#a16207',
                                }}>
                                    {detail.is_send ? '✓ Sent' : '⏳ Pending'}
                                </span>
                            </div>

                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                        English
                                    </div>
                                    <h5 className="fw-bold mb-3" style={{ fontSize: '1.1rem', color: '#111827', lineHeight: 1.4 }}>
                                        {detail.title || '—'}
                                    </h5>
                                    <p style={{ fontSize: '14.5px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>
                                        {detail.message || '—'}
                                    </p>
                                </div>

                                <div className="col-md-6" dir="rtl">
                                    <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                        Arabic (العربية)
                                    </div>
                                    <h5 className="fw-bold mb-3" style={{ fontSize: '1.1rem', color: '#111827', lineHeight: 1.4 }}>
                                        {detail.title_ar || '—'}
                                    </h5>
                                    <p style={{ fontSize: '14.5px', color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>
                                        {detail.message_ar || '—'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recipients panel — only when resolved list exists */}
                    {resolved.length > 0 && (
                        <RecipientsPanel
                            recipients={resolved}
                            title={
                                detail.type === 'specific'
                                    ? 'Recipients'
                                    : 'Additional Specific Recipients'
                            }
                            subtitle={
                                detail.type !== 'specific' ? mixedSubtitle : null
                            }
                        />
                    )}
                </div>

                {/* Right col — summary + timeline */}
                <div className="col-lg-4">

                    {/* Summary */}
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
                        <div className="card-body p-4">
                            <h6 className="fw-semibold mb-3" style={{ color: '#111827' }}>Summary</h6>

                            <div className="d-flex flex-column gap-3">

                                {detail.type === 'All' && (
                                    <SummaryRow label="Sent To" value="All Users" valueBg="#dcfce7" valueColor="#15803d" />
                                )}

                                {detail.type === 'tutor' && (
                                    <>
                                        <SummaryRow label="All Tutors" value="Everyone" valueBg="#dbeafe" valueColor="#1d4ed8" />
                                        {resolved.length > 0 && (
                                            <SummaryRow label="+ Specific Parents" value={resolved.length} valueBg="#fce7f3" valueColor="#be185d" />
                                        )}
                                    </>
                                )}

                                {detail.type === 'parent' && (
                                    <>
                                        <SummaryRow label="All Parents" value="Everyone" valueBg="#fce7f3" valueColor="#be185d" />
                                        {resolved.length > 0 && (
                                            <SummaryRow label="+ Specific Tutors" value={resolved.length} valueBg="#dbeafe" valueColor="#1d4ed8" />
                                        )}
                                    </>
                                )}

                                {detail.type === 'specific' && (
                                    <>
                                        <SummaryRow label="Total Recipients" value={resolved.length} valueBg="#f1f5f9" valueColor="#374151" />
                                        {tutorCount > 0 && (
                                            <SummaryRow label="Tutors" value={tutorCount} valueBg="#dbeafe" valueColor="#1d4ed8" />
                                        )}
                                        {parentCount > 0 && (
                                            <SummaryRow label="Parents" value={parentCount} valueBg="#fce7f3" valueColor="#be185d" />
                                        )}
                                    </>
                                )}

                                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }} />

                                <SummaryRow
                                    label="Status"
                                    value={detail.is_send ? 'Sent' : 'Pending'}
                                    valueBg={detail.is_send ? '#dcfce7' : '#fef9c3'}
                                    valueColor={detail.is_send ? '#15803d' : '#a16207'}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="card border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                        <div className="card-body p-4">
                            <h6 className="fw-semibold mb-3" style={{ color: '#111827' }}>Timeline</h6>
                            <div className="d-flex flex-column gap-3">
                                <TimelineRow label="Created At" value={formatDate(detail.createdAt)} />
                                <TimelineRow label="Updated At" value={formatDate(detail.updatedAt)} />
                                {detail.scheduled_at && (
                                    <TimelineRow label="Scheduled At" value={formatDate(detail.scheduled_at)} highlight />
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    )
}

// ─── Small reusable components ────────────────────────────────────────────────
const SummaryRow = ({ label, value, valueBg, valueColor }) => (
    <div className="d-flex align-items-center justify-content-between">
        <span style={{ fontSize: 13, color: '#6b7280' }}>{label}</span>
        <span style={{
            fontSize: 12, fontWeight: 700, padding: '3px 10px',
            borderRadius: 20, backgroundColor: valueBg, color: valueColor,
        }}>
            {value}
        </span>
    </div>
)

const TimelineRow = ({ label, value, highlight }) => (
    <div>
        <div style={{
            fontSize: 11, fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2,
        }}>
            {label}
        </div>
        <div style={{ fontSize: 13, color: highlight ? '#0d6efd' : '#374151', fontWeight: highlight ? 600 : 400 }}>
            {value}
        </div>
    </div>
)

export default ViewNotification